import type { RuleId } from '../../core';

/**
 * Human-readable headlines for each QC rule, shown in the issue list and fix
 * announcements instead of the raw engine slug (e.g. `max-cps` → "Reading
 * speed"). A `Record<RuleId, string>` so adding a rule fails the build until it
 * has a label. Mirrors the role of {@link PRESET_LABELS} for presets.
 */
export const RULE_LABELS: Record<RuleId, string> = {
  'max-chars-per-line': 'Line too long',
  'max-lines-per-cue': 'Too many lines',
  'max-cps': 'Reading speed',
  'min-duration': 'Duration too short',
  'max-duration': 'Duration too long',
  'min-gap': 'Gap too small',
  overlap: 'Overlapping cues',
  'reversed-timing': 'Reversed timing',
  'empty-cue': 'Empty cue',
  'duplicate-cue': 'Duplicate cue',
  'forbidden-characters': 'Forbidden characters',
  'punctuation-spacing': 'Punctuation spacing',
  'unclosed-tag': 'Unclosed tag',
  'cue-count-monotonic-timing': 'Out-of-order cues',
  'bidi-punctuation': 'Punctuation direction (RTL)',
  'mixed-digit-style': 'Mixed digit style',
  'latin-run-isolation': 'Unmarked Latin text',
  'arabic-line-length': 'Line too long (Arabic)',
  'kashida-strip': 'Kashida characters',
  'ellipsis-style': 'Three-dot ellipsis',
};

/** The human label for a rule, falling back to the raw id if unmapped. */
export function ruleLabel(id: RuleId): string {
  return RULE_LABELS[id] ?? id;
}

/**
 * Manual-remedy hints (cue-forge-n3e.4) for rules whose violations the operator
 * must resolve by editing the cue — the flag-only rules with no auto-fix, plus
 * `max-cps` (its CPS assist declines when there is no gap to grow into). Each
 * names the concrete action ("split", "shorten", "reorder", …) so a flagged
 * card tells the operator what to do, not just what is wrong. Rules absent from
 * this map have an auto-fix; their Fix button is the remedy, so no hint shows.
 */
const RULE_REMEDIES: Partial<Record<RuleId, string>> = {
  'max-cps': 'Shorten the text, or extend the cue so it stays on screen longer.',
  // max-chars-per-line is auto-fixable in context (re-wrap), but the fix is
  // declined when the cue is already at its max-lines limit (cue-forge-8b4) —
  // so guidance must exist for the no-room case, and reads sensibly when Fix
  // is available too.
  'max-chars-per-line': 'Shorten the line, or split the cue across two or more cues.',
  'max-lines-per-cue': 'Split the cue across two or more cues.',
  'duplicate-cue': 'Remove the repeat, or rewrite it so it differs from the cue before.',
  'arabic-line-length': 'Shorten the line, or break it across lines.',
  'cue-count-monotonic-timing': 'Reorder the cues so their timings run in sequence.',
};

/** The manual remedy hint for a rule, or `''` when none applies (auto-fixable). */
export function ruleRemedy(id: RuleId): string {
  return RULE_REMEDIES[id] ?? '';
}
